import { memo } from "react";
import { Handle, Position, type NodeProps } from "@xyflow/react";
import { GripVertical } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { roleLabel } from "@/lib/claimRoles";

export interface ClaimNodeData {
  text: string;
  claimType: "thesis" | "supporting" | "counter" | "synthesis" | "gap";
  status: "draft" | "supported" | "contested";
  evidenceCount: number;
  selected?: boolean;
}

// Nodes are role-NEUTRAL: relations own the saturated color palette (on the
// edges), so claim cards stay calm and convey role via a text chip instead of
// a colored border. The one exception is "gap" (Open question) — violet is
// relation-free, so open-question claims still pop on the canvas.
const GAP_CARD = "border-violet-400 bg-violet-50 dark:border-violet-800 dark:bg-violet-950/30";
const NEUTRAL_CARD = "border-border bg-card";

const STATUS_DOTS: Record<string, string> = {
  draft: "bg-gray-400",
  supported: "bg-green-500",
  contested: "bg-amber-500",
};

function ClaimNodeInner({ data }: NodeProps) {
  const d = data as unknown as ClaimNodeData;
  const isGap = d.claimType === "gap";
  const cardColor = isGap ? GAP_CARD : NEUTRAL_CARD;
  const dotColor = STATUS_DOTS[d.status] ?? "bg-gray-400";

  return (
    <div
      className={`relative cursor-crosshair rounded-lg border-2 ${cardColor} px-3 py-2 shadow-sm ${
        d.selected ? "ring-2 ring-primary ring-offset-1" : ""
      }`}
      style={{ minWidth: 160, maxWidth: 220 }}
    >
      {/* Single whole-node connection handle. One handle (not top/bottom) means
          drag-to-connect is unambiguous: ConnectionMode.Loose makes the node you
          start on the source and the node you release on the target, so edge
          direction always follows the drag. Transparent + full-cover so the
          entire card is the grab/drop target. Clicks still bubble to
          onNodeClick (handle pointerdown doesn't preventDefault), and
          connectOnClick is disabled, so a click selects instead of connecting. */}
      <Handle
        type="source"
        position={Position.Top}
        title="Drag to another claim to link · click to inspect"
        className="!min-h-0 !min-w-0 !rounded-lg !border-0 !bg-transparent"
        style={{ position: "absolute", inset: 0, width: "100%", height: "100%", transform: "none", opacity: 0, zIndex: 1 }}
      />

      {/* Role chip (text, not color) */}
      <div className="mb-1 flex items-center justify-between gap-1">
        <div className="flex min-w-0 items-center gap-1">
          {/* Drag grip — the ONLY move target. Stacked above the full-cover
              connection Handle (z-[2] > handle's z-1) so a pointerdown here
              starts a node move via ReactFlow's dragHandle=".claim-drag-handle"
              instead of a connection. Dragging the card body still draws a link. */}
          <span
            className="claim-drag-handle relative z-[2] -ml-1 shrink-0 cursor-grab text-muted-foreground/40 hover:text-muted-foreground active:cursor-grabbing"
            title="Drag to move"
            aria-label="Drag to move claim"
          >
            <GripVertical className="size-3" />
          </span>
          <span
            className={`truncate rounded-full px-1.5 py-0 text-[9px] font-medium uppercase tracking-wide ${
              isGap
                ? "bg-violet-100 text-violet-700 dark:bg-violet-900/40 dark:text-violet-300"
                : "bg-muted text-muted-foreground"
            }`}
          >
            {roleLabel(d.claimType)}
          </span>
        </div>
        {d.evidenceCount > 0 && (
          <Badge variant="secondary" className="shrink-0 text-[10px] px-1 py-0">
            {d.evidenceCount}
          </Badge>
        )}
      </div>

      <p className="text-xs leading-tight line-clamp-3">{d.text}</p>

      <div className="mt-1 flex items-center gap-1">
        <span className={`inline-block size-2 rounded-full ${dotColor}`} />
        <span className="text-[10px] text-muted-foreground capitalize">{d.status}</span>
      </div>
    </div>
  );
}

export const ClaimNode = memo(ClaimNodeInner);
